import { ReportService } from 'data/services';
import { ReportServiceInterface } from 'domain/services';
import { makeExportSuspectedManipulation } from 'main/factories/usecases/reports/export-suspected-manipulation-factory';
import { makeExportSuspectedStudentsRanking } from 'main/factories/usecases/reports/export-suspected-students-ranking-factory';
import { makeGetSuspectedManipulationCount } from 'main/factories/usecases/reports/get-suspected-manipulation-count-factory';
import { makeActivityPendingByProfessionals } from 'main/factories/usecases/reports/remote-activities-pending-by-professionals';
import { makeActivitiesSummaryByTask } from 'main/factories/usecases/reports/remote-activities-summary-by-task';
import { makeExportAccessReportExcel } from 'main/factories/usecases/reports/remote-export-access-report-excel-factory';
import { makeExportActivitiesQuantityPerStudent } from 'main/factories/usecases/reports/remote-export-activities-quantity-per-student-factory';
import { makeExportPendingActivitiesPerProfessionalExcel } from 'main/factories/usecases/reports/remote-export-pending-activities-per-professional-excel';
import { makeExportPendingActivitiesPerProfessionalPdf } from 'main/factories/usecases/reports/remote-export-pending-activities-per-professional-pdf';
import { makeExportPerformanceReportForProfessionalsExcel } from 'main/factories/usecases/reports/remote-export-performance-report-for-professionals-excel-factory';
import { makeExportProfessionalsDailyWorkloadExcel } from 'main/factories/usecases/reports/remote-export-professionals-daily-workload-excel-factory';
import { makeGetAppRegisteredStudentsPerGroup } from 'main/factories/usecases/reports/remote-get-app-registered-students-per-group-factory';
import { makeGetConsolidatedActivitiesByStudent } from 'main/factories/usecases/reports/remote-get-consolidated-activities-by-student-factory';
import {
  makeGetNumberStudentsPerProfessionalInField,
  makeExportNumberStudentsPerProfessionalInField,
} from 'main/factories/usecases/reports/remote-get-number-students-per-professional-in-field-factory';
import { makeGetPendingActivitiesPerProfessionalSummary } from 'main/factories/usecases/reports/remote-get-pending-activities-per-professional-summary';
import { makeGetPerformanceReportForProfessionalsEvolution } from 'main/factories/usecases/reports/remote-get-performance-report-for-professionals-evolution-factory';
import { makeGetPerformanceReportForProfessionalsRanking } from 'main/factories/usecases/reports/remote-get-performance-report-for-professionals-ranking-factory';
import { makeGetPerformanceReportForProfessionalsSummary } from 'main/factories/usecases/reports/remote-get-performance-report-for-professionals-summary-factory';
import { makeGetPerformanceReportForProfessionalsTopValidatedActivities } from 'main/factories/usecases/reports/remote-get-performance-report-for-professionals-top-validated-activities-factory';
import { makeGetProfessionalsDailyWorkload } from 'main/factories/usecases/reports/remote-get-professionals-daily-workload-factory';
import { makeGetStudentsFrequencyReport } from 'main/factories/usecases/reports/remote-get-students-frequncy-factory';
import { makeStudentsIndividualWorkloadReport } from 'main/factories/usecases/reports/remote-get-students-individual-workload-report-factory';
import {
  makeGetStudentsWorkloadAchievement,
  makeExportStudentsWorkloadAchievement,
} from 'main/factories/usecases/reports/remote-get-students-workload-achievement-factory';
import { makeGetSuspectedManipulation } from 'main/factories/usecases/reports/remote-get-suspected-manipulation-factory';
import { makeGetSuspectedStudentsRanking } from 'main/factories/usecases/reports/remote-get-suspected-students-ranking-factory';
import { makeTeamReportList } from 'main/factories/usecases/reports/remote-get-team-report-list-factory';
import { makeHandleActivitiesAnalyticalPerGroup } from 'main/factories/usecases/reports/remote-handle-activities-analytical-per-group-factory';
import { makeHandleActivitiesAnalyticalPerTask } from 'main/factories/usecases/reports/remote-handle-activities-analytical-per-task-factory';
import { makeHandleActivitiesSummarizedByStudent } from 'main/factories/usecases/reports/remote-handle-activities-summarized-by-student-factory';
import { makeHandleMissedActivitiesJustifications } from 'main/factories/usecases/reports/remote-handle-missed-activities-justifications-factory';
import {
  makeGetProfessionalsWorkJourney,
  makeHandleProfessionalsWorkJourney,
} from 'main/factories/usecases/reports/remote-handle-professionals-work-journey-factory';
import { makeHandleRejectedByProfessionals } from 'main/factories/usecases/reports/remote-handle-rejected-by-professionals-factory';
import { makeHandleStudentsNoAppUsageUse } from 'main/factories/usecases/reports/remote-handle-students-no-app-usage-factory';
import { makeHandleSummaryReportByInternshipLocation } from 'main/factories/usecases/reports/remote-handle-summary-report-by-internship-location-factory';
import { makeHandleTeamReport } from 'main/factories/usecases/reports/remote-handle-team-report-factory';
import { makeHandleStudentsWithoutRegisteringApp } from 'main/factories/usecases/reports/remote-students-without-registering-app-factory';
import { makeSummaryReportByGroup } from 'main/factories/usecases/reports/remote-summary-report-by-group';
import { makeHandleValidatedByProfessionals } from 'main/factories/usecases/reports/remote-validated-by-professionals-factory';
import { makeGetStudentReportDailyWorkload } from 'main/factories/usecases/reports/student-report-daily-workload-factory';

export const makeReportService = (): ReportServiceInterface => {
  return new ReportService(
    makeGetConsolidatedActivitiesByStudent(),
    makeGetStudentsFrequencyReport(),
    makeGetProfessionalsWorkJourney(),
    makeHandleProfessionalsWorkJourney(),
    makeStudentsIndividualWorkloadReport(),
    makeExportActivitiesQuantityPerStudent(),
    makeHandleActivitiesAnalyticalPerTask(),
    makeGetStudentReportDailyWorkload(),
    makeHandleActivitiesSummarizedByStudent(),
    makeSummaryReportByGroup(),
    makeActivitiesSummaryByTask(),
    makeHandleSummaryReportByInternshipLocation(),
    makeHandleActivitiesAnalyticalPerGroup(),
    makeHandleStudentsWithoutRegisteringApp(),
    makeHandleStudentsNoAppUsageUse(),
    makeHandleValidatedByProfessionals(),
    makeHandleRejectedByProfessionals(),
    makeGetAppRegisteredStudentsPerGroup(),
    makeGetNumberStudentsPerProfessionalInField(),
    makeExportNumberStudentsPerProfessionalInField(),
    makeActivityPendingByProfessionals(),
    makeGetPendingActivitiesPerProfessionalSummary(),
    makeExportPendingActivitiesPerProfessionalPdf(),
    makeExportPendingActivitiesPerProfessionalExcel(),
    makeHandleMissedActivitiesJustifications(),
    makeTeamReportList(),
    makeHandleTeamReport(),
    makeGetPerformanceReportForProfessionalsSummary(),
    makeGetPerformanceReportForProfessionalsTopValidatedActivities(),
    makeGetPerformanceReportForProfessionalsEvolution(),
    makeGetPerformanceReportForProfessionalsRanking(),
    makeExportPerformanceReportForProfessionalsExcel(),
    makeGetProfessionalsDailyWorkload(),
    makeExportProfessionalsDailyWorkloadExcel(),
    makeExportAccessReportExcel(),
    makeGetSuspectedManipulation(),
    makeGetSuspectedManipulationCount(),
    makeGetSuspectedStudentsRanking(),
    makeExportSuspectedManipulation(),
    makeExportSuspectedStudentsRanking(),
    makeGetStudentsWorkloadAchievement(),
    makeExportStudentsWorkloadAchievement(),
  );
};
